import React, { useEffect, useRef, useState } from "react";
import { api, clientUrl } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Upload, ArrowUp, ArrowDown } from "lucide-react";
import { Header } from "./TextsTab";

export default function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState("");
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/clients"); setClients(data); }
    catch (e) { toast.error("No se pudieron cargar los clientes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !name) return toast.error("Completá nombre y archivo");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      await api.post("/admin/clients", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo subido");
      setName("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    try { await api.delete(`/admin/clients/${id}`); toast.success("Cliente eliminado"); await load(); }
    catch (e) { toast.error("Error"); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= clients.length) return;
    const newArr = [...clients];
    [newArr[idx], newArr[j]] = [newArr[j], newArr[idx]];
    setClients(newArr);
    try { await api.put("/admin/reorder", { collection: "clients", ordered_ids: newArr.map((p) => p.id) }); }
    catch (e) { toast.error("Error al reordenar"); }
  };

  const updateName = (c, v) => setClients((arr) => arr.map((x) => x.id === c.id ? { ...x, name: v } : x));
  const saveName = async (c) => {
    try { await api.put(`/admin/clients/${c.id}`, { name: c.name, filename: c.filename, order: c.order }); toast.success("Nombre guardado"); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <Header title="Clientes" subtitle="Subí logos de clientes (PNG/SVG con fondo transparente recomendado)." />
      <form onSubmit={upload} className="glass rounded-2xl p-6 grid sm:grid-cols-[1fr_auto] gap-4 items-end">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#9bb0e0] mb-1.5">Nombre</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: La Casa Streaming" className="w-full bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#5fa0ff]" />
          </div>
          <div>
            <label className="block text-xs text-[#9bb0e0] mb-1.5">Logo</label>
            <input ref={fileRef} type="file" accept="image/*" className="w-full text-sm text-[#cad8ff] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1a3a8a] file:text-white" />
          </div>
        </div>
        <button type="submit" disabled={uploading} className="btn-primary px-5 py-2.5 rounded-lg inline-flex items-center gap-2 font-medium disabled:opacity-60">
          <Upload className="w-4 h-4" /> {uploading ? "Subiendo…" : "Agregar"}
        </button>
      </form>

      {loading ? <div className="text-[#7da3ff] mono">Cargando…</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {clients.map((c, idx) => (
            <div key={c.id} className="glass rounded-xl p-4 flex items-center gap-4">
              <div className="w-20 h-14 rounded-lg bg-[rgba(10,20,55,0.5)] flex items-center justify-center flex-shrink-0 border border-[rgba(120,160,255,0.15)]">
                <img src={clientUrl(c)} alt={c.name} className="max-w-full max-h-full object-contain" style={{ filter: "brightness(0) invert(1)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <input value={c.name} onChange={(e) => updateName(c, e.target.value)} onBlur={() => saveName(c)} className="w-full bg-transparent text-white text-sm focus:outline-none border-b border-transparent focus:border-[#5fa0ff]" />
                <div className="mono text-[9px] text-[#5b73a8] truncate mt-1">{c.filename}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(idx, -1)} className="w-7 h-7 rounded glass flex items-center justify-center" disabled={idx === 0}><ArrowUp className="w-3 h-3 text-[#9ec0ff]" /></button>
                <button onClick={() => move(idx, 1)} className="w-7 h-7 rounded glass flex items-center justify-center" disabled={idx === clients.length-1}><ArrowDown className="w-3 h-3 text-[#9ec0ff]" /></button>
              </div>
              <button onClick={() => del(c.id)} className="w-8 h-8 rounded-lg glass flex items-center justify-center text-[#ff8a8a]"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
